/**
 * Lifecycle.ts — SKCATALOG-CC-002 CR-7, console side.
 *
 * SKCATALOG-DB-001 §4: "Illegal transitions aren't offered as options rather
 * than erroring after the fact." So the console asks this module which actions to
 * render, instead of rendering everything and letting the API answer 409.
 *
 * Mirrors the backend map (src/services/lifecycle.ts). ARCHIVED is terminal — it
 * is what this catalog uses in place of a hard delete, so walking it back would
 * let an item removed for compliance reasons reappear on a menu.
 */

import type { LifecycleState } from '../schemas/catalog/common';

const ALLOWED: Record<LifecycleState, LifecycleState[]> = {
  DRAFT:       ['PUBLISHED', 'DEACTIVATED', 'ARCHIVED'],
  PUBLISHED:   ['DRAFT', 'DEACTIVATED', 'ARCHIVED'],
  DEACTIVATED: ['DRAFT', 'PUBLISHED', 'ARCHIVED'],
  ARCHIVED:    [],
};

/** Can the item move from `from` to `to`? A no-op is always legal (idempotent). */
export function canTransition(from: LifecycleState, to: LifecycleState): boolean {
  if (from === to) return true;
  return (ALLOWED[from] ?? []).includes(to);
}

/** The legal destinations from a state — drives which menu items render. */
export function allowedTransitions(from: LifecycleState): LifecycleState[] {
  return ALLOWED[from] ?? [];
}

/** Is this item in a state in which it can be sold? Mirrors CR-6's gate:
 *  PUBLISHED is the only sellable state, so a DRAFT reads as not-sellable too. */
export function isSellableState(state: LifecycleState | string | undefined): boolean {
  return state === 'PUBLISHED';
}

/** Plain-language "why not", for the not-sellable note beside a price. */
export function notSellableReason(state: LifecycleState | string | undefined): string | null {
  switch (state) {
    case 'PUBLISHED':   return null;
    case 'DRAFT':       return 'Draft — not sellable until published.';
    case 'DEACTIVATED': return 'Deactivated — not sellable.';
    case 'ARCHIVED':    return 'Archived — not sellable.';
    default:            return null;
  }
}
